import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Row,
  Col,
  Form,
  Button,
  Alert,
  Badge,
  Modal,
  Card,
} from "react-bootstrap";
import BootstrapDate from "./BootstrapDate";
import "../stepper.css";
import { useNavigate } from "react-router-dom";

/* ------- helpers ------- */
const initialFormData = {
  firstName: "",
  lastName: "",
  mobile: "",
  email: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  dob: "",
  ssn: "",
  startDate: "",
  employmentType: "",
  position: "",
  availabilityDays: [],
  availabilityShifts: [],
  over18: "",
  eligible: "",
  felony: "",
  felonyDetails: "",
};

const toCsv = (arr) => (Array.isArray(arr) ? arr.join(",") : "");
const yesNoToBool = (v) => (v === "Yes" ? true : v === "No" ? false : null);
const last4 = (v) =>
  typeof v === "string" ? v.replace(/\D/g, "").slice(-4) : null;
const toISO = (v) => {
  if (!v) return null;
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear(),
    mm = String(d.getMonth() + 1).padStart(2, "0"),
    dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const days = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const shifts = ["Day Shift", "Afternoon Shift", "Other"];

/* === validators & formatters for phone/email === */
const PHONE_UI_RE = /^\+1-\d{3}-\d{3}-\d{4}$/; // UI must match +1-123-456-7890
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const onlyDigits = (s) => (s || "").replace(/\D/g, "");
// take up to 10 US digits, stripping an optional leading 1
const toUS10 = (s) => {
  let d = onlyDigits(s);
  if (d.startsWith("1")) d = d.slice(1);
  return d.slice(0, 10);
};
const formatUSPhoneUI = (s) => {
  const d = toUS10(s);
  if (!d) return "";
  if (d.length <= 3) return `+1-${d}`;
  if (d.length <= 6) return `+1-${d.slice(0, 3)}-${d.slice(3)}`;
  return `+1-${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6, 10)}`;
};
// backend value: +1XXXXXXXXXX
const toE164US = (s) => {
  const d = toUS10(s);
  return d.length === 10 ? `+1${d}` : null;
};

export default function MultiStepApplication() {
  const [addrLoading, setAddrLoading] = useState(false);
  const [addrUSPS, setAddrUSPS] = useState(null);
  const [addrErr, setAddrErr] = useState("");
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [serverOk, setServerOk] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  const [applicationId, setApplicationId] = useState(null);

  const topRef = useRef(null);
  const firstNameRef = useRef(null);
  const navigate = useNavigate();

  const steps = useMemo(
    () => [
      { key: "account", title: "Personal Details" },
      { key: "personal", title: "Address Information" },
      { key: "dates", title: "Dates & ID" },
      { key: "job", title: "Job Details" },
      { key: "avail", title: "Availability" },
      { key: "legal", title: "Legal" },
      { key: "review", title: "Review & Submit" },
    ],
    []
  );

  const progressPct = (step / (steps.length - 1)) * 100;

  useEffect(() => {
    if (step === 0) {
      setTimeout(() => {
        try {
          firstNameRef.current?.focus({ preventScroll: true });
        } catch {
          const x = window.scrollX;
          const y = window.scrollY;
          firstNameRef.current?.focus();
          window.scrollTo(x, y);
        }
      }, 120);
    }
  }, [step]);

  const scrollTop = () =>
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  /* phone-specific change handler (auto-format) */
  const handlePhoneChange = (e) => {
    const formatted = formatUSPhoneUI(e.target.value);
    setFormData((prev) => ({ ...prev, mobile: formatted }));
    setErrors((prev) => ({ ...prev, mobile: "" }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (
      type === "checkbox" &&
      (name === "availabilityDays" || name === "availabilityShifts")
    ) {
      setFormData((prev) => ({
        ...prev,
        [name]: checked
          ? [...prev[name], value]
          : prev[name].filter((x) => x !== value),
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (!formData.firstName) e.firstName = "First name is required.";
      if (!formData.lastName) e.lastName = "Last name is required.";

      // phone + email validations
      if (!formData.mobile) {
        e.mobile = "Mobile number is required.";
      } else if (!PHONE_UI_RE.test(formData.mobile)) {
        e.mobile = "Use format: +1-123-456-7890";
      }
      if (!formData.email) {
        e.email = "Email is required.";
      } else if (!EMAIL_RE.test(formData.email)) {
        e.email = "Enter a valid email address.";
      }
    } else if (s === 1) {
      if (!formData.address) e.address = "Address is required.";
      if (!formData.city) e.city = "City is required.";
      if (!formData.state) e.state = "State is required.";
      if (!formData.zip) e.zip = "ZIP is required.";
    } else if (s === 2) {
      if (!formData.dob) e.dob = "Date of birth is required.";
      if (!formData.ssn || formData.ssn.replace(/\D/g, "").length !== 4)
        e.ssn = "Enter last 4 digits.";
      if (!formData.startDate) e.startDate = "Start date is required.";
    } else if (s === 3) {
      if (!formData.employmentType)
        e.employmentType = "Select employment type.";
      if (!formData.position) e.position = "Position is required.";
    } else if (s === 4) {
      if (formData.availabilityDays.length === 0)
        e.availabilityDays = "Select at least one day.";
      if (formData.availabilityShifts.length === 0)
        e.availabilityShifts = "Select at least one shift.";
    } else if (s === 5) {
      if (!formData.over18) e.over18 = "Please confirm.";
      if (!formData.eligible) e.eligible = "Please confirm.";
      if (!formData.felony) e.felony = "Please answer.";
      if (formData.felony === "Yes" && !formData.felonyDetails)
        e.felonyDetails = "Please provide details.";
    }
    setErrors(e);
    return e;
  };

  const next = () => {
    const e = validateStep(step);
    if (Object.keys(e).length) {
      scrollTop();
      if (e.firstName) setTimeout(() => firstNameRef.current?.focus(), 120);
      return;
    }
    setStep((s) => Math.min(s + 1, steps.length - 1));
    scrollTop();
  };
  const back = () => {
    setStep((s) => Math.max(s - 1, 0));
    scrollTop();
  };

  const submit = async () => {
    const allErrs = [0, 1, 2, 3, 4, 5].reduce(
      (acc, s) => ({ ...acc, ...validateStep(s) }),
      {}
    );
    if (Object.keys(allErrs).length) {
      setStep(0);
      scrollTop();
      setTimeout(() => firstNameRef.current?.focus(), 120);
      return;
    }

    // normalize phone to E.164 before sending
    const normalizedPhone = toE164US(formData.mobile);
    if (!normalizedPhone) {
      setErrors((prev) => ({
        ...prev,
        mobile: "Use format: +1-123-456-7890",
      }));
      setStep(0);
      scrollTop();
      return;
    }

    const payload = {
      ApplicationId: null,
      FirstName: formData.firstName.trim(),
      LastName: formData.lastName.trim(),
      MobileNumber: normalizedPhone, // send +1XXXXXXXXXX to backend
      EmailAddress: formData.email || null,
      AddressLine1: formData.address || null,
      City: formData.city || null,
      State: formData.state || null,
      Zip: formData.zip || null,
      DateOfBirth: toISO(formData.dob),
      SSNLast4: last4(formData.ssn),
      DesiredEmploymentType: formData.employmentType || null,
      AvailabilityDays: toCsv(formData.availabilityDays),
      AvailabilityShifts: toCsv(
        formData.availabilityShifts.map((s) => s.replace(" Shift", ""))
      ),
      StartDate: toISO(formData.startDate),
      PositionApplyingFor: formData.position || null,
      Over18: yesNoToBool(formData.over18),
      EligibleToWorkUS: yesNoToBool(formData.eligible),
      FelonyConvicted: yesNoToBool(formData.felony),
    };

    const allowed = ["Full-time", "Part-time", "Temporary"];
    if (
      payload.DesiredEmploymentType &&
      !allowed.includes(payload.DesiredEmploymentType)
    ) {
      payload.DesiredEmploymentType = null;
    }

    setSubmitting(true);
    setServerError("");
    setServerOk("");
    try {
      const res = await axios.post("/api/employment-apply.php", payload, {
        headers: { "Content-Type": "application/json" },
      });
      const json = res.data;
      const ok = json?.success ?? json?.ok ?? false;
      if (!ok) throw new Error(json?.message || "Save failed");

      const newId = json?.ApplicationId ?? json?.id ?? null;
      setApplicationId(newId);
      setShowSuccess(true);
      setServerOk("");

      setFormData(initialFormData);
      setStep(0);
      scrollTop();
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || "Save failed";
      setServerError(msg);
      setStep(0);
      scrollTop();
      setTimeout(() => firstNameRef.current?.focus(), 120);
    } finally {
      setSubmitting(false);
    }
  };

  const validateAddressUSPS = async () => {
    // Need at least street + (ZIP or City+State)
    const streetOk = (formData.address || "").trim().length > 0;
    const zipOk = (formData.zip || "").trim().length >= 5;
    const cityStateOk =
      (formData.city || "").trim() && (formData.state || "").trim();

    if (!streetOk || (!zipOk && !cityStateOk)) return;

    setAddrLoading(true);
    setAddrErr("");
    setAddrUSPS(null);
    try {
      const res = await axios.post(
        "/api/usps-validate.php",
        {
          address: formData.address,
          city: formData.city || undefined,
          state: formData.state || undefined,
          zip: formData.zip || undefined,
        },
        { headers: { "Content-Type": "application/json" }, timeout: 10000 }
      );
      if (!res.data?.success)
        throw new Error(res.data?.message || "USPS failed");

      const std = res.data.standardized || {};
      // Auto-fill city/state/zip from USPS (street left as user typed unless they click "Use USPS Address")
      setFormData((prev) => ({
        ...prev,
        city: std.city || prev.city,
        state: std.state || prev.state,
        zip: std.zip5 || prev.zip,
      }));
      setAddrUSPS(std);
    } catch (e) {
      setAddrErr(e.message || "USPS validation error");
    } finally {
      setAddrLoading(false);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setServerOk("");
    setApplicationId(null);
    navigate("/");
  };

  return (
    <Card ref={topRef} className="shadow-sm">
      <Card.Body>
        {/* STEPPER HEADER */}
        <div className="stepper" style={{ "--progress": progressPct }}>
          <div
            className="stepper-progress"
            style={{ width: `${progressPct}%` }}
          />
          {steps.map((s, i) => {
            const state =
              i < step ? "completed" : i === step ? "active" : "upcoming";
            return (
              <div key={s.key} className={`step ${state}`}>
                <div className="circle">{i < step ? "✓" : i + 1}</div>
                <div className="label">{s.title}</div>
              </div>
            );
          })}
        </div>

        {serverError && (
          <Alert variant="danger" className="mb-3">
            {serverError}
          </Alert>
        )}
        {!showSuccess && serverOk && (
          <Alert variant="success" className="mb-3">
            {serverOk}
          </Alert>
        )}

        {/* STEP 0: Personal Details */}
        {step === 0 && (
          <>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group controlId="firstName">
                  <Form.Label>
                    First Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    ref={firstNameRef}
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    isInvalid={!!errors.firstName}
                    placeholder="First name"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.firstName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="lastName">
                  <Form.Label>
                    Last Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    isInvalid={!!errors.lastName}
                    placeholder="Last name"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.lastName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mt-1">
              <Col md={6}>
                <Form.Group controlId="mobile">
                  <Form.Label>
                    Mobile <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    inputMode="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneChange}
                    isInvalid={!!errors.mobile}
                    placeholder="+1-123-456-7890"
                    pattern="\+1-\d{3}-\d{3}-\d{4}"
                    title="Format: +1-123-456-7890"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.mobile}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="email">
                  <Form.Label>
                    Email <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    inputMode="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    isInvalid={!!errors.email}
                    placeholder="you@example.com"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </>
        )}

        {/* STEP 1: Address */}
        {step === 1 && (
          <>
            <Form.Group className="mt-1" controlId="address">
              <Form.Label>
                Address <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                name="address"
                value={formData.address}
                onChange={handleChange}
                isInvalid={!!errors.address}
                placeholder="Street address"
              />
              <Form.Control.Feedback type="invalid">
                {errors.address}
              </Form.Control.Feedback>
            </Form.Group>

            <Row className="g-3 mt-1">
              <Col md={4}>
                <Form.Control
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  isInvalid={!!errors.city}
                  placeholder="City"
                />
                <div className="invalid-feedback d-block">{errors.city}</div>
              </Col>
              <Col md={4}>
                <Form.Control
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  isInvalid={!!errors.state}
                  placeholder="State"
                />
                <div className="invalid-feedback d-block">{errors.state}</div>
              </Col>
              <Col md={4}>
                <Form.Control
                  name="zip"
                  value={formData.zip}
                  onChange={handleChange}
                  isInvalid={!!errors.zip}
                  placeholder="ZIP"
                />
                <div className="invalid-feedback d-block">{errors.zip}</div>
              </Col>
            </Row>
          </>
        )}

        {/* STEP 2: Dates & ID */}
        {step === 2 && (
          <>
            <Row className="g-3">
              <Col md={6}>
                <BootstrapDate
                  label="Date of Birth"
                  name="dob"
                  value={formData.dob}
                  onChange={handleChange}
                  error={errors.dob}
                  required
                  min={"1900-01-01"}
                  max={new Date().toISOString().slice(0, 10)}
                />
              </Col>
              <Col md={6}>
                <Form.Group controlId="ssn">
                  <Form.Label>
                    SSN (last 4) <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="ssn"
                    value={formData.ssn}
                    onChange={handleChange}
                    maxLength={4}
                    isInvalid={!!errors.ssn}
                    placeholder="1234"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.ssn}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mt-3">
              <BootstrapDate
                label="When can you start?"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                error={errors.startDate}
                required
                min={new Date().toISOString().slice(0, 10)}
              />
            </Form.Group>
          </>
        )}

        {/* STEP 3: Job Details */}
        {step === 3 && (
          <>
            <Form.Group className="mt-1">
              <Form.Label>
                Desired Employment Type <span className="text-danger">*</span>
              </Form.Label>
              <div className="d-flex gap-4">
                {["Full-time", "Part-time", "Temporary"].map((t) => (
                  <Form.Check
                    key={t}
                    type="radio"
                    name="employmentType"
                    id={`emp-${t}`}
                    label={t}
                    value={t}
                    checked={formData.employmentType === t}
                    onChange={handleChange}
                    isInvalid={!!errors.employmentType}
                  />
                ))}
              </div>
              {errors.employmentType && (
                <div className="invalid-feedback d-block">
                  {errors.employmentType}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>
                Position <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                name="position"
                value={formData.position}
                onChange={handleChange}
                isInvalid={!!errors.position}
                placeholder="e.g. Caregiver"
              />
              <Form.Control.Feedback type="invalid">
                {errors.position}
              </Form.Control.Feedback>
            </Form.Group>
          </>
        )}

        {/* STEP 4: Availability */}
        {step === 4 && (
          <>
            <Form.Group className="mt-1">
              <Form.Label>
                Availability (Days){" "}
                <Badge bg="secondary">choose all that apply</Badge>
              </Form.Label>
              <Row xs={2} md={4}>
                {days.map((d) => (
                  <Col key={d} className="mb-1">
                    <Form.Check
                      type="checkbox"
                      id={`day-${d}`}
                      label={d}
                      name="availabilityDays"
                      value={d}
                      checked={formData.availabilityDays.includes(d)}
                      onChange={handleChange}
                    />
                  </Col>
                ))}
              </Row>
              {errors.availabilityDays && (
                <div className="invalid-feedback d-block">
                  {errors.availabilityDays}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>Availability (Shifts)</Form.Label>
              <div className="d-flex flex-wrap gap-4">
                {shifts.map((s) => (
                  <Form.Check
                    key={s}
                    type="checkbox"
                    id={`shift-${s}`}
                    label={s}
                    name="availabilityShifts"
                    value={s}
                    checked={formData.availabilityShifts.includes(s)}
                    onChange={handleChange}
                  />
                ))}
              </div>
              {errors.availabilityShifts && (
                <div className="invalid-feedback d-block">
                  {errors.availabilityShifts}
                </div>
              )}
            </Form.Group>
          </>
        )}

        {/* STEP 5: Legal */}
        {step === 5 && (
          <>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    Are you above the age of 18?{" "}
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="over18"
                    value={formData.over18}
                    onChange={handleChange}
                    isInvalid={!!errors.over18}
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.over18}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    Legally eligible for employment in USA?{" "}
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="eligible"
                    value={formData.eligible}
                    onChange={handleChange}
                    isInvalid={!!errors.eligible}
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.eligible}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mt-3">
              <Form.Label>
                Have you been convicted of a felony or aggravated misdemeanor?{" "}
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                name="felony"
                value={formData.felony}
                onChange={handleChange}
                isInvalid={!!errors.felony}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.felony}
              </Form.Control.Feedback>

              {formData.felony === "Yes" && (
                <Form.Control
                  className="mt-2"
                  name="felonyDetails"
                  placeholder="If yes, please explain"
                  value={formData.felonyDetails}
                  onChange={handleChange}
                  isInvalid={!!errors.felonyDetails}
                />
              )}
              {errors.felonyDetails && (
                <div className="invalid-feedback d-block">
                  {errors.felonyDetails}
                </div>
              )}
            </Form.Group>
          </>
        )}

        {/* STEP 6: Review */}
        {step === 6 && (
          <div className="mt-2">
            <h6 className="mb-3">Review</h6>
            <Row className="g-2">
              <Col md={6}>
                <strong>Name:</strong> {formData.firstName} {formData.lastName}
              </Col>
              <Col md={6}>
                <strong>Contact:</strong> {formData.mobile} · {formData.email}
              </Col>
              <Col md={12}>
                <strong>Address:</strong> {formData.address}, {formData.city},{" "}
                {formData.state} {formData.zip}
              </Col>
              <Col md={6}>
                <strong>DOB:</strong> {formData.dob}
              </Col>
              <Col md={6}>
                <strong>Start Date:</strong> {formData.startDate}
              </Col>
              <Col md={6}>
                <strong>Employment:</strong> {formData.employmentType}
              </Col>
              <Col md={6}>
                <strong>Position:</strong> {formData.position}
              </Col>
              <Col md={12}>
                <strong>Days:</strong>{" "}
                {formData.availabilityDays.join(", ") || "—"}
              </Col>
              <Col md={12}>
                <strong>Shifts:</strong>{" "}
                {formData.availabilityShifts.join(", ") || "—"}
              </Col>
              <Col md={6}>
                <strong>Over 18:</strong> {formData.over18 || "—"}
              </Col>
              <Col md={6}>
                <strong>Eligible:</strong> {formData.eligible || "—"}
              </Col>
              <Col md={12}>
                <strong>Felony:</strong> {formData.felony || "—"}
                {formData.felony === "Yes" && formData.felonyDetails
                  ? ` — ${formData.felonyDetails}`
                  : ""}
              </Col>
            </Row>
          </div>
        )}

        {/* NAV BUTTONS */}
        <div
          className={`d-flex mt-4 ${
            step === 0 ? "justify-content-end" : "justify-content-between"
          }`}
        >
          {step > 0 && (
            <Button
              variant="outline-secondary"
              onClick={back}
              disabled={submitting}
            >
              Back
            </Button>
          )}

          {step < steps.length - 1 ? (
            <Button variant="success" onClick={next} disabled={submitting}>
              Next
            </Button>
          ) : (
            <Button variant="success" onClick={submit} disabled={submitting}>
              {submitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  />
                  Submitting...
                </>
              ) : (
                "Submit Application"
              )}
            </Button>
          )}
        </div>
      </Card.Body>

      {/* Success Modal */}
      <Modal show={showSuccess} onHide={handleCloseSuccess} centered>
        <Modal.Header closeButton>
          <Modal.Title>Application Submitted</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Application form submitted successfully. One of our team members
            will get back to you for next steps!
          </p>
          {applicationId && (
            <p className="mb-0">
              <strong>Your Application ID:</strong>{" "}
              <code className="px-2 py-1 rounded bg-light">
                {applicationId}
              </code>
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleCloseSuccess}>Okay</Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}
